import { useEffect, useState } from 'react'
import { ref, onValue } from 'firebase/database'
import { db, isConfigured } from './firebase.js'

/** RTDB reports an unauthorized read as PERMISSION_DENIED. Signed in, but not
 *  as the account the rules allow — distinct from not being signed in, and the
 *  only one the user can act on. */
const isPermissionDenied = (err) =>
  /permission[_ ]denied/i.test(err?.message || '') || err?.code === 'PERMISSION_DENIED'

const PATHS = [
  ['/listings', 'listings'],
  ['/meta', 'meta'],
  ['/notes', 'notes'],
  ['/dealers', 'dealers'],
  ['/criteria', 'criteria'],
]

/**
 * One slot per path, not one shared {loading,error,denied} for all five.
 *
 * Five subscriptions resolve independently. A single shared field means a
 * success on one path unconditionally clears whatever an earlier failure on
 * a *different* path had recorded — a denied /dealers read papered over by a
 * /listings read landing microseconds later. That is exactly the "failed
 * read looks like real data" failure this hook exists to prevent, so each
 * path's status, value and error are tracked separately and only merged for
 * the return value.
 *
 * `loadedOnce` marks whether this slot has EVER reported `status: 'ok'`. It
 * is what lets a caller tell a first-read failure (nothing to show yet, so
 * the default `{}` must not be trusted as real data) apart from a later
 * refresh failure on a path that already has a good value on screen — the
 * value itself is kept either way (see the error branch in the subscribe
 * effect below), but only `loadedOnce` says whether that value is real.
 */
const initialSlots = () =>
  Object.fromEntries(
    PATHS.map(([, key]) => [key, { status: 'pending', value: {}, error: null, loadedOnce: false }])
  )

/** Merge per-path slots into the hook's public shape. loading stays true
 *  until every slot has resolved one way or another — a consumer must never
 *  see a "done loading" workspace that is still missing 4 of 5 nodes. */
function toPublicState(slots) {
  const entries = Object.entries(slots)
  const loading = entries.some(([, s]) => s.status === 'pending')
  const denied = entries.some(([, s]) => s.status === 'denied')
  const errored = entries.find(([, s]) => s.status === 'error')
  const values = Object.fromEntries(entries.map(([key, s]) => [key, s.value]))
  // A path is "ready" once its current value can be trusted: it just
  // succeeded, or it succeeded at least once before and is only failing on a
  // later refresh now (the value stays the last-known-good one in that case
  // — see below). A path that has never once succeeded is NOT ready, even
  // though its slot's default value is the same empty `{}` a genuinely empty
  // node would have: a caller must not treat "failed and never loaded" the
  // same as "loaded and genuinely empty".
  const readyPaths = Object.fromEntries(entries.map(([key, s]) => [key, s.status === 'ok' || s.loadedOnce]))
  return {
    loading,
    error: errored ? errored[1].error : null,
    errorPath: errored ? errored[0] : null,
    denied,
    readyPaths,
    ...values,
  }
}

/**
 * Every RTDB subscription the app needs, in one place.
 *
 * Subscribed per-path, never at the root: a root read is denied outright
 * because /fcmTokens is unreadable by every client, which would look like an
 * authorization failure even for the allowed account.
 */
export function useWingfinderData(user) {
  const [slots, setSlots] = useState(initialSlots)
  // Keyed on the uid, not the user object: a parent that hands us a freshly
  // built object on every render (same signed-in account, new reference)
  // must not tear down and resubscribe five listeners for nothing.
  const uid = user?.uid

  useEffect(() => {
    if (!isConfigured || !uid) return
    // Reset on every (re)subscribe, not just on mount — switching to a
    // different signed-in account must not keep showing the previous
    // account's data or errors while the new subscriptions spin up.
    setSlots(initialSlots())
    const stops = PATHS.map(([path, key]) =>
      onValue(
        ref(db, path),
        (snap) =>
          setSlots((s) => ({
            ...s,
            [key]: { status: 'ok', value: snap.val() || {}, error: null, loadedOnce: true },
          })),
        (err) =>
          setSlots((s) => ({
            ...s,
            [key]: isPermissionDenied(err)
              ? { status: 'denied', value: s[key].value, error: null, loadedOnce: s[key].loadedOnce }
              : { status: 'error', value: s[key].value, error: err.message, loadedOnce: s[key].loadedOnce },
          }))
      )
    )
    return () => stops.forEach((stop) => stop())
  }, [uid])

  return toPublicState(slots)
}
